import React from "react";
import TreatmentVarianceModal from "../modals/TreatmentVarianceModal";
import { MdNavigateNext } from "react-icons/md";

/**
 * Process historical data to extract treatment statistics
 * @param {Array} historicalData - Raw historical data array
 * @returns {Array} - Processed treatment data with statistics
 */
const processHistoricalTreatmentData = (historicalData) => {
  if (!historicalData || historicalData.length === 0) {
    return []
  }

  // Accumulate treatments across all days
  const treatmentMap = new Map()

  historicalData.forEach(day => {
    if (day.treatment_duration_variance?.treatments) {
      day.treatment_duration_variance.treatments.forEach(treatment => {
        const key = treatment.appointmentType || treatment.name

        if (!key) return // Skip if no identifier

        if (!treatmentMap.has(key)) {
          // First time seeing this treatment
          treatmentMap.set(key, {
            name: treatment.name || treatment.appointmentType,
            appointmentType: treatment.appointmentType,
            scheduledDuration: treatment.scheduledDuration || 0,
            actualDurations: treatment.actualDurations || [],
            totalAppointments: treatment.actualDurations?.length || 0
          })
        } else {
          // Update existing treatment data
          const existingTreatment = treatmentMap.get(key)

          // Combine actual durations
          if (treatment.actualDurations && treatment.actualDurations.length > 0) {
            existingTreatment.actualDurations = [
              ...existingTreatment.actualDurations,
              ...treatment.actualDurations
            ]
          }

          // Update total appointments count
          existingTreatment.totalAppointments +=
            treatment.actualDurations?.length || 0
        }
      })
    }
  })

  // Convert the map to an array and calculate averages
  const treatmentsArray = Array.from(treatmentMap.values()).map(treatment => {
    // Calculate average actual duration
    const actualDurations = treatment.actualDurations || []
    const avgActualDuration =
      actualDurations.length > 0
        ? actualDurations.reduce((sum, d) => sum + d.duration, 0) /
          actualDurations.length
        : 0

    return {
      name: treatment.name,
      appointmentType: treatment.appointmentType,
      scheduledDuration: treatment.scheduledDuration,
      actualDuration: Math.round(avgActualDuration),
      totalAppointments: treatment.totalAppointments,
      actualDurations: treatment.actualDurations
    }
  })

  return treatmentsArray
}

/**
 * Creates props for treatment variance analysis to be used with BlankCard component
 * @param {Array} historicalData - Raw historical data array
 * @param {Object} options - Additional options like height, chartHeight, etc.
 * @returns {Object} - Props object ready to spread into BlankCard component
 */
export const createTreatmentVariance = (historicalData, options = {}) => {
  // Process the historical data first
  const data = processHistoricalTreatmentData(historicalData);
  
  // Set default values for options
  const {
    height = 400,
    chartHeight = 300,
    threshold = 20, // Percentage threshold for significant variations
    sampleSizeThreshold = 20, // Minimum sample size to consider a treatment
    limit = 3, // Number of top variations to display
    selectedRange = "1month",
    setSelectedRange = () => {},
  } = options;

  // Now the raw data is just the original historical data
  const rawData = historicalData;

  // Filter out treatments with insufficient sample size
  const filteredData = data.filter(item => {
    const sampleSize = item.totalAppointments || item.actualDurations?.length || 0;
    return sampleSize > sampleSizeThreshold;
  });

  // Process the data to calculate variances
  const dataWithVariance = filteredData.map(item => {
    const percentDifference = item.scheduledDuration > 0 
      ? ((item.actualDuration - item.scheduledDuration) / item.scheduledDuration * 100).toFixed(1)
      : 0;
    
    return {
      ...item,
      percentDifference: parseFloat(percentDifference),
      recommendation: Math.ceil(item.actualDuration / 10) * 10
    };
  });

  // Get treatments with significant variations (beyond threshold)
  const significantVariations = dataWithVariance.filter(
    item => Math.abs(item.percentDifference) > threshold
  );

  // Get top N treatments with highest variation (absolute value)
  const topVariations = dataWithVariance
    .sort((a, b) => Math.abs(b.percentDifference) - Math.abs(a.percentDifference))
    .slice(0, limit);

  // Define table columns
  const columns = [
    { key: 'name', label: 'Treatment' },
    { key: 'scheduledDuration', label: 'Scheduled (min)', isNumeric: true },
    { key: 'actualDuration', label: 'Actual (min)', isNumeric: true },
    { key: 'recommendation', label: 'Recommendation (min)', isNumeric: true },
    // {
    //     key: "percentDifference",
    //     label: "Variance",
    //     isNumeric: true,
    //     render: item => (
    //         <PercentageBadge value={item.percentDifference} isHigherBetter={false} />
    //     )
    // }
  ];

  // Get status and badge based on the number of significant variations
  const getStatus = () => {
    if (significantVariations.length <= 5) {
      return {
        status: "great",
        badge: "Looks Good"
      };
    } else if (significantVariations.length <= 10) {
      return {
        status: "good",
        badge: "Warning"
      };
    } else {
      return {
        status: "critical",
        badge: "Needs Attention"
      };
    }
  };

  const statusInfo = getStatus();

  // Create description based on the number of significant variations
  const description = significantVariations.length > 0
    ? `You have <strong>${significantVariations.length} treatment${significantVariations.length === 1 ? '' : 's'}</strong> with optimized block length recommendations. Here ${significantVariations.length === 1 ? 'is' : 'are'} the top <strong>${Math.min(limit, significantVariations.length)}</strong> that need attention:`
    : `All <strong>treatments</strong> are within expected scheduling parameters.`;

  // Format the data for ActualScheduledDistribution component
  const formattedRawData = rawData.length > 0 ? formatDataForDistribution(rawData, filteredData) : [];

  // Create modal element with props - pass the raw data
  const modalElement = (
    <TreatmentVarianceModal
      data={formattedRawData.length > 0 ? formattedRawData : rawData} // Pass formatted data if available
      processedData={filteredData} // Pass the filtered processed data
      threshold={threshold}
      selectedRange={selectedRange}
      setSelectedRange={setSelectedRange}
      initialTreatment={topVariations.length > 0 ? topVariations[0].appointmentType || topVariations[0].name : null}
    />
  );

  // Helper function to format data for ActualScheduledDistribution
  function formatDataForDistribution(rawData, processedData) {
    // If rawData is empty, return empty array
    if (!rawData || rawData.length === 0) return [];
    
    // Check if rawData already has the expected format
    const hasExpectedFormat = rawData.some(day => 
      day && day.treatment_duration_variance && Array.isArray(day.treatment_duration_variance.treatments)
    );
    
    if (hasExpectedFormat) return rawData;
    
    // Format the data for ActualScheduledDistribution
    return rawData.map(day => {
      return {
        ...day,
        treatment_duration_variance: {
          treatments: processedData.map(treatment => {
            // Create a random set of actualDurations if none exist
            const actualDurations = treatment.actualDurations || 
              Array(treatment.totalAppointments || 5).fill().map(() => ({
                duration: treatment.actualDuration + (Math.random() * 20 - 10) // Random variation around actual duration
              }));
            
            return {
              appointmentType: treatment.appointmentType || treatment.name,
              name: treatment.name,
              scheduledDuration: treatment.scheduledDuration,
              actualDurations: actualDurations
            };
          })
        }
      };
    });
  }

  // Create a custom footer with the View More Details button
  const customFooter = ({ openModal }) => (
    <div className="w-full flex justify-end py-2">
      <button
        onClick={openModal}
        className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-blue-600 hover:text-blue-800"
      >
        View More Details
        <MdNavigateNext className="ml-1 h-4 w-4" />
      </button>
    </div>
  );

  // Return props for BlankCard
  return {
    title: "Treatment Duration Analysis",
    description,
    chartType: "table",
    chartData: topVariations,
    chartHeight,
    height,
    status: statusInfo.status,
    badge: statusInfo.badge,
    options: {
      columns: columns,
      emptyMessage: `No treatments found with sufficient sample size (>${sampleSizeThreshold})`,
      allowRowClickModal: true // Allow row clicks to open modal too
    },
    modal: modalElement, // Pass the modal component
    enableModal: true, // Enable the modal functionality
    customFooter: (props) => customFooter(props) // Use the custom footer we created
  };
};

export default createTreatmentVariance; 